/**
 * Lovwel — Cube/L-corner artifact remover
 * Last-resort JS pass that runs after page load + on DOM mutations.
 * Targets:
 *   - Shopify's <svg class="placeholder-svg"> rendered via placeholder_svg_tag
 *   - Empty placeholder containers
 *   - Stray SVGs inside product cards / card galleries that don't carry an icon class
 *   - Any element with a black square inline style and no content
 *
 * Why JS as well as CSS? Some of these elements are injected after CSS load
 * (e.g. via JS card-gallery rendering) and slip past selector matches.
 */
(function () {
  'use strict';

  var DEBUG = false;
  var SAFE_ICON_PATTERN = /(^|\s)(icon|svg-icon|chevron|caret|arrow|button-icon|spinner|loader|swatch__|badge|wishlist|heart|search|cart|menu|close)/i;

  function isCardArea(el) {
    if (!el || !el.closest) return false;
    return !!el.closest('.product-card, [class*="product-card"], .product-grid, .product-grid__item, .card-gallery, [class*="card-gallery"], .product-media-container, .resource-image, .media-wrapper, .collection-card, [class*="collection-card"]');
  }

  function isPlaceholderSvg(svg) {
    if (!svg) return false;
    var cls = (svg.getAttribute('class') || '').toLowerCase();
    if (/(^|\s)placeholder/.test(cls)) return true;
    // Some Shopify-rendered placeholders have no class but a viewBox we recognize
    if (/^[a-z\s]*$/.test(cls) && cls.indexOf('icon') === -1) {
      var inner = svg.innerHTML || '';
      if (inner.length < 800 && /<path|<rect|<circle/.test(inner) && !SAFE_ICON_PATTERN.test(cls)) {
        return isCardArea(svg);
      }
    }
    return false;
  }

  function killCubes(root) {
    root = root || document;
    if (!root.querySelectorAll) return;

    // 0. NUCLEAR: kill quick-add-component (the actual cube — confirmed via Playwright DOM inspection of live site)
    root.querySelectorAll('quick-add-component, .quick-add, .quick-add__button, .quick-add__product-form-component, [class*="quick-add"]:not([class*="quick-add-bar"])').forEach(function (el) {
      el.style.display = 'none';
      el.setAttribute('hidden', 'true');
      try { el.remove(); } catch (e) { /* element may be already detached */ }
      if (DEBUG) console.log('[cube-killer] removed quick-add', el);
    });

    // 0b. v17 NEW: detect COLLAPSED .card-gallery elements by COMPUTED size.
    // The cube on collection-page cards without images: .card-gallery shrinks to ~22x12
    // because Horizon's --badge-top-left modifier styles + missing img dimensions
    // collapse the wrapper. CSS `:not(:has(img))` doesn't work because the gallery
    // DOES have <img> children (just rendered at 0x0). Only computed-size check works.
    root.querySelectorAll('.card-gallery, [class*="card-gallery"]').forEach(function (g) {
      try {
        var r = g.getBoundingClientRect();
        // Collapsed cube: tiny but non-zero
        if (r.width > 0 && r.width < 60 && r.height > 0 && r.height < 60) {
          // Check if any child img has actual rendered size — if not, the gallery is bogus
          var imgs = g.querySelectorAll('img');
          var hasRenderedImg = false;
          for (var i = 0; i < imgs.length; i++) {
            var ir = imgs[i].getBoundingClientRect();
            if (ir.width > 30 && ir.height > 30) { hasRenderedImg = true; break; }
          }
          if (!hasRenderedImg) {
            g.style.display = 'none';
            if (DEBUG) console.log('[cube-killer] hid collapsed card-gallery', g);
          }
        }
      } catch (e) { /* skip */ }
    });

    // 1. Direct: any svg.placeholder-svg
    root.querySelectorAll('svg.placeholder-svg, .placeholder-svg').forEach(function (el) {
      el.style.display = 'none';
      if (DEBUG) console.log('[cube-killer] hid placeholder-svg', el);
    });

    // 2. Resource-image / collection-card placeholders — neutralize background, hide inner svg
    root.querySelectorAll('.resource-image--placeholder, .collection-card__image--placeholder, .featured-blog-posts-card__image--placeholder, .card-gallery__placeholder').forEach(function (el) {
      el.style.background = '#f5f5f5';
      el.querySelectorAll('svg').forEach(function (s) { s.style.display = 'none'; });
    });

    // 3. Stray SVGs inside card areas without an icon class
    root.querySelectorAll('svg').forEach(function (svg) {
      if (!isCardArea(svg)) return;
      var cls = svg.getAttribute('class') || '';
      if (SAFE_ICON_PATTERN.test(cls)) return;
      // If the svg is small + sits at top-left of a card without being an icon, kill it
      var rect = svg.getBoundingClientRect ? svg.getBoundingClientRect() : null;
      if (rect && rect.width > 0 && rect.width < 60 && rect.height < 60) {
        // Check parent isn't a button (icons inside buttons are kept)
        if (!svg.closest('button, [role="button"], a.button, .quick-add')) {
          svg.style.display = 'none';
          if (DEBUG) console.log('[cube-killer] hid stray small svg', svg);
        }
      } else if (isPlaceholderSvg(svg)) {
        svg.style.display = 'none';
        if (DEBUG) console.log('[cube-killer] hid placeholder by signature', svg);
      }
    });

    // 4. Black-bg empty placeholders
    root.querySelectorAll('[style*="background-color: rgb(0, 0, 0)"], [style*="background:#000"], [style*="background: #000"]').forEach(function (el) {
      if (el.children.length === 0 && (el.textContent || '').trim() === '') {
        el.style.display = 'none';
      }
    });

    // 5. Empty media-wrapper without img → neutral gray
    root.querySelectorAll('.product-card .media-wrapper, .product-card-gallery, .resource-image').forEach(function (el) {
      if (!el.querySelector('img[src]:not([src=""])')) {
        el.style.background = '#f5f5f5';
        el.querySelectorAll('svg').forEach(function (s) { s.style.display = 'none'; });
      }
    });
  }

  function run() {
    try { killCubes(document); } catch (e) { if (DEBUG) console.warn('[cube-killer]', e); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  // Re-run on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', run);
  document.addEventListener('shopify:section:select', run);

  // MutationObserver for client-rendered cards (collection infinite scroll, product recommendations, etc.)
  if ('MutationObserver' in window) {
    var pending = false;
    var mo = new MutationObserver(function (mutations) {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          if (m.addedNodes && m.addedNodes.length) {
            m.addedNodes.forEach(function (n) {
              if (n.nodeType === 1) {
                killCubes(n);
                killCssLeaks(n);
              }
            });
          }
        }
      });
    });
    mo.observe(document.body || document.documentElement, { childList: true, subtree: true });
  }

  /**
   * Aggressive defense: walk every Text node via TreeWalker and strip any
   * substring matching ":root { }", ":root { ... }", "--var: ...;" or
   * ".class { ... }". Targets text inside elements with other children
   * (which simple element-iteration misses). Runs on load + section reload
   * + periodically for 10 seconds after load (catches lazy-injected text). */
  var CSS_LEAK_PATTERN = /:root\s*\{|^\s*--[\w-]+\s*:\s|^\s*\.[\w-]+\s*\{/;

  function killCssLeaks(root) {
    root = root || document.body;
    if (!root || !root.ownerDocument) return;
    try {
      var walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
      var killed = 0;
      var node;
      var nodesToProcess = [];
      while ((node = walker.nextNode())) nodesToProcess.push(node);
      for (var i = 0; i < nodesToProcess.length; i++) {
        node = nodesToProcess[i];
        var parent = node.parentElement;
        if (!parent) continue;
        var pt = parent.tagName;
        if (pt === 'STYLE' || pt === 'SCRIPT' || pt === 'HEAD' || pt === 'TEMPLATE' || pt === 'NOSCRIPT') continue;
        var t = node.nodeValue || '';
        if (!CSS_LEAK_PATTERN.test(t)) continue;
        var cleaned = t.replace(/:root\s*\{[^}]*\}|:root\s*\{\s*\}|--[\w-]+\s*:[^;]*;?|\.[\w-]+\s*\{[^}]*\}/g, '');
        if (cleaned.trim().length === 0) {
          node.nodeValue = '';
          if ((parent.textContent || '').trim().length === 0 && !parent.querySelector('img, svg, video, iframe')) {
            parent.style.display = 'none';
          }
        } else {
          node.nodeValue = cleaned;
        }
        killed++;
      }
      if (DEBUG && killed > 0) console.log('[cube-killer] stripped', killed, 'CSS leaks');
    } catch (e) {}
  }

  /**
   * v47: Nuke empty <style data-shopify> blocks outright. Horizon emits ~27
   * of them per collection page (one before each <product-card>). User-Agent
   * stylesheet hides them, but iOS Safari has been observed rendering them
   * as visible text. Removing them entirely is safe — they contain only
   * whitespace. */
  function stripEmptyStyleBlocks(root) {
    root = root || document;
    if (!root.querySelectorAll) return;
    try {
      root.querySelectorAll('style').forEach(function (s) {
        var content = (s.textContent || '').trim();
        if (content.length === 0) {
          try { s.remove(); } catch (e) { s.style.display = 'none'; }
        }
      });
    } catch (e) {}
  }

  function runLeakSweep() {
    stripEmptyStyleBlocks(document);
    killCssLeaks(document.body);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runLeakSweep, { once: true });
  } else {
    runLeakSweep();
  }
  document.addEventListener('shopify:section:load', runLeakSweep);
  // Aggressive periodic sweep: every 500ms for first 10s after load.
  var sweepCount = 0;
  var sweepInterval = setInterval(function () {
    runLeakSweep();
    if (++sweepCount >= 20) clearInterval(sweepInterval);
  }, 500);
})();
